import { bindConsent, hasConsent } from "services/cookieConsent";
import { SessionStorageKeys } from "utils/storageKeys";

/**
 * Marketing attribution for the visit that led to a sign-up: the campaign
 * parameters and ad click ids on the URL, the first outside referrer, the landing
 * path, and HubSpot's visitor cookie. Captured on bootstrap and sent with sign-up
 * so the server can tie a new account to what brought it here.
 *
 * Stored in two places: sessionStorage for the tab, always; and the first-party
 * cookie `tl_attr`, so a visitor who returns within 90 days keeps the touch. The
 * cookie is functional storage for the sign-up the visitor is in the middle of,
 * and it is written only once marketing consent is granted or implied
 * (`bindAttributionCookie`), and removed when that consent is withdrawn. It is set
 * on `.talaera.com` so the touch survives a hop between marketing subdomains and
 * the app.
 */

export type Attribution = Partial<Record<AttributionKey, string>>;

type AttributionKey =
  | (typeof CAMPAIGN_KEYS)[number]
  | (typeof FIRST_TOUCH_KEYS)[number]
  | "hutk";

// Merge rule. Campaign parameters and click ids are last-touch, key by key: a later
// visit carrying one replaces the stored value, so the paid click closest to sign-up
// wins. The referrer and landing path are first-touch and are never replaced once
// stored. An absent or empty value never clears a stored one.
const CAMPAIGN_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  // Offer variant (`?v=` on the URL).
  "variant",
] as const;
const FIRST_TOUCH_KEYS = ["referrer", "landing_path"] as const;
const QUERY_PARAM: Partial<Record<string, string>> = { variant: "v" };
const ALL_KEYS: readonly AttributionKey[] = [
  ...CAMPAIGN_KEYS,
  ...FIRST_TOUCH_KEYS,
  "hutk",
];

export const ATTRIBUTION_COOKIE = "tl_attr";
const HUBSPOT_COOKIE = "hubspotutk";
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
// The server's limit per value.
const MAX_VALUE_LENGTH = 512;
// Browsers drop a cookie whose name, value and attributes pass 4 KB, silently.
const MAX_COOKIE_VALUE_LENGTH = 3800;

let cookieAllowed = false;

export function captureAttribution(
  location: Pick<Location, "href" | "hostname"> = window.location,
  referrer: string = document.referrer
) {
  const next: Attribution = { ...getAttribution() };
  const url = new URL(location.href);

  for (const key of CAMPAIGN_KEYS) {
    const value = truncate(url.searchParams.get(QUERY_PARAM[key] ?? key));
    if (value) {
      next[key] = value;
    }
  }

  if (!next.referrer) {
    const externalReferrer = truncate(externalReferrerOf(referrer, location.hostname));
    if (externalReferrer) {
      next.referrer = externalReferrer;
    }
  }
  if (!next.landing_path) {
    // The path alone: a query string can carry a sign-in or reset token.
    next.landing_path = truncate(url.pathname) ?? undefined;
  }

  const hubspotVisitor = truncate(readCookie(HUBSPOT_COOKIE));
  if (hubspotVisitor) {
    next.hutk = hubspotVisitor;
  }

  writeSessionStorage(next);
  if (cookieAllowed) {
    writeCookie(next);
  }
}

export function getAttribution(): Attribution | null {
  return (
    parseAttribution(readSessionStorage()) ??
    parseAttribution(readCookie(ATTRIBUTION_COOKIE))
  );
}

export type ConsentedAttribution = Attribution & { consent: "granted" | "denied" };

// The touch with whether marketing consent stands, for a receiver that keeps the
// click ids only under consent.
export function getConsentedAttribution(): ConsentedAttribution | null {
  const attribution = getAttribution();
  return (
    attribution && {
      ...attribution,
      consent: hasConsent("marketing") ? "granted" : "denied",
    }
  );
}

// Writes the cookie while the visitor's marketing consent stands, and clears it
// when it is withdrawn.
export function bindAttributionCookie() {
  bindConsent(
    "marketing",
    () => {
      cookieAllowed = true;
      const attribution = getAttribution();
      if (attribution) {
        writeCookie(attribution);
      }
    },
    () => {
      cookieAllowed = false;
      clearCookie();
    }
  );
}

export function attributionCookieAttributes(
  location: Pick<Location, "hostname" | "protocol">,
  maxAgeSeconds = COOKIE_MAX_AGE_SECONDS
) {
  const attributes = [`max-age=${maxAgeSeconds}`, "path=/", "SameSite=Lax"];
  if (location.protocol === "https:") {
    attributes.push("Secure");
  }
  if (location.hostname === "talaera.com" || location.hostname.endsWith(".talaera.com")) {
    attributes.push("domain=.talaera.com");
  }
  return attributes.join("; ");
}

function writeSessionStorage(attribution: Attribution) {
  try {
    window.sessionStorage.setItem(
      SessionStorageKeys.attribution,
      JSON.stringify(attribution)
    );
  } catch {
    // Storage can be unavailable in private browsing modes; the cookie carries it where
    // marketing consent allows.
  }
}

function writeCookie(attribution: Attribution) {
  const value = fitCookieValue(attribution);
  document.cookie = `${ATTRIBUTION_COOKIE}=${value}; ${attributionCookieAttributes(window.location)}`;
}

function clearCookie() {
  document.cookie = `${ATTRIBUTION_COOKIE}=; ${attributionCookieAttributes(window.location, 0)}`;
}

function fitCookieValue(attribution: Attribution) {
  let entries = Object.entries(attribution);
  let value = encodeURIComponent(JSON.stringify(Object.fromEntries(entries)));
  while (value.length > MAX_COOKIE_VALUE_LENGTH && entries.length > 0) {
    const [longest] = entries.reduce((a, b) => (b[1].length > a[1].length ? b : a));
    entries = entries.filter(([key]) => key !== longest);
    value = encodeURIComponent(JSON.stringify(Object.fromEntries(entries)));
  }
  return value;
}

// A referrer counts as external when it is another site altogether: a hop between
// Talaera's own subdomains is not the touch that brought the visitor.
function externalReferrerOf(referrer: string, hostname: string) {
  if (!referrer) {
    return null;
  }
  let referrerHost: string;
  try {
    referrerHost = new URL(referrer).hostname;
  } catch {
    return null;
  }
  const isTalaera = (host: string) =>
    host === "talaera.com" || host.endsWith(".talaera.com");
  if (referrerHost === hostname || (isTalaera(referrerHost) && isTalaera(hostname))) {
    return null;
  }
  return referrer;
}

function parseAttribution(serialized: string | null): Attribution | null {
  if (!serialized) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const attribution: Attribution = {};
  for (const key of ALL_KEYS) {
    const value = (parsed as Record<string, unknown>)[key];
    const text = truncate(typeof value === "string" ? value : null);
    if (text) {
      attribution[key] = text;
    }
  }
  return Object.keys(attribution).length > 0 ? attribution : null;
}

function truncate(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return null;
  }
  return trimmed.slice(0, MAX_VALUE_LENGTH);
}

function readSessionStorage() {
  try {
    return window.sessionStorage.getItem(SessionStorageKeys.attribution);
  } catch {
    return null;
  }
}

function readCookie(name: string) {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split(";")
    .map(part => part.trim())
    .find(part => part.startsWith(prefix));
  if (!entry) {
    return null;
  }
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return null;
  }
}
